import { useEffect, useCallback, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { supabase, type Item, type Category } from '../lib/supabase'
import { applyRealtimeChange, applyUpdate, insertOptimistic, restoreAt, toggleDone } from '../lib/items'
import { revertRow, snapshotRow, type RowSnapshot } from '../lib/rows'
import { isListFullError } from '../lib/billing'
import { haptic } from '../lib/haptics'
import { HAPTICS } from '../lib/constants'
import { useIsVisible } from './useIsVisible'
import { itemHistoryKey } from './useItemHistory'
import { RECIPES_KEY } from './useRecipes'

const ITEMS_KEY = (householdId: string) => ['items', householdId]
const CATS_KEY  = (householdId: string) => ['categories', householdId]
// Every optimistic item write, so settling can tell whether others are still out.
const WRITES_KEY = (householdId: string) => ['items-write', householdId]

export interface UseItemsOptions {
  /**
   * Called when the server refuses an open item because the free list is full
   * (15 open items, 042_free_plus.sql, PT413). The list screen stops what it
   * can see locally; this covers what it can't — an add made offline, a trial
   * that ended while the tab was open, or another phone taking the last place.
   */
  onListFull?: () => void
}

type AddVars = { id: string; name: string; category_id?: string | null; quantity?: string | null; note?: string | null }

// A write naming a category that was deleted meanwhile (on another phone,
// delete_category, 049) fails on the foreign key.
const FOREIGN_KEY_VIOLATION = '23503'
const missingCategory = (error: { code?: string } | null, categoryId: string | null | undefined) =>
  !!categoryId && error?.code === FOREIGN_KEY_VIOLATION

// Rows per soft-delete request in finishItems: about 3.7 kB of ids in the URL.
const FINISH_CHUNK = 100

export function useItems(householdId: string, memberId: string, options: UseItemsOptions = {}) {
  const qc = useQueryClient()
  const isVisible = useIsVisible()
  const { onListFull } = options
  // Adds a full free list refused. Never dropped silently (the pricing spec):
  // the list screen shows them as "Not saved, list full" with Upgrade and
  // Remove, and retryUnsaved writes them once there is room. In memory only —
  // like any unsent draft, a reload lets them go.
  const [unsaved, setUnsaved] = useState<Item[]>([])
  // A write cancels a read in flight, or its answer would overwrite the
  // optimistic row; cancelling puts back the list from before that read. Coming
  // back from the background, that read was the catch-up for everything
  // realtime missed — so once the writes settle, the list is read again.
  const cancelledRead = useRef(false)

  const holdReads = useCallback(async () => {
    if (qc.isFetching({ queryKey: ITEMS_KEY(householdId) })) cancelledRead.current = true
    await qc.cancelQueries({ queryKey: ITEMS_KEY(householdId) })
  }, [qc, householdId])

  /** `self`: writes of this hook's own still counted as pending (a mutation's onSettled counts itself). */
  const readAgainIfCancelled = useCallback((self = 1) => {
    if (!cancelledRead.current || qc.isMutating({ mutationKey: WRITES_KEY(householdId) }) > self) return
    cancelledRead.current = false
    void qc.invalidateQueries({ queryKey: ITEMS_KEY(householdId) })
  }, [qc, householdId])

  /** A failed write takes back its own row only (lib/items.ts revertRow). */
  const revert = useCallback((id: string, snap: RowSnapshot<Item> | undefined) => {
    if (snap) qc.setQueryData<Item[]>(ITEMS_KEY(householdId), old => revertRow(old ?? [], id, snap))
  }, [qc, householdId])

  /** The optimistic row follows a write that went to Other (missingCategory). */
  const moveToOther = useCallback((id: string) => {
    qc.setQueryData<Item[]>(ITEMS_KEY(householdId),
      old => (old ?? []).map(i => (i.id === id ? { ...i, category_id: null } : i)))
  }, [qc, householdId])

  // ── queries ─────────────────────────────────────────────────────────────────

  const itemsQuery = useQuery({
    queryKey: ITEMS_KEY(householdId),
    enabled: !!householdId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('items').select('*')
        .eq('household_id', householdId)
        .is('deleted_at', null)
        .order('sort_order').order('created_at')
      if (error) throw error
      return data as Item[]
    },
  })

  const catsQuery = useQuery({
    queryKey: CATS_KEY(householdId),
    enabled: !!householdId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories').select('*')
        .eq('household_id', householdId)
        .order('sort_order')
      if (error) throw error
      return data as Category[]
    },
  })

  // ── realtime ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return

    const channel = supabase
      .channel(`items:${householdId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'items',
          filter: `household_id=eq.${householdId}` },
        (payload) => {
          qc.setQueryData<Item[]>(ITEMS_KEY(householdId), (old = []) => {
            const type = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE'
            const row = (type === 'DELETE' ? payload.old : payload.new) as Item
            return applyRealtimeChange(old, { type, row })
          })
        })
      // A category added, renamed, reordered or deleted on another phone.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'categories',
          filter: `household_id=eq.${householdId}` },
        () => { void qc.invalidateQueries({ queryKey: CATS_KEY(householdId) }) })
      // Joined, or joined again after the socket dropped or the tab was hidden
      // (the channel is closed then): read what realtime missed meanwhile.
      .subscribe(status => {
        if (status !== 'SUBSCRIBED') return
        void qc.invalidateQueries({ queryKey: ITEMS_KEY(householdId) })
        void qc.invalidateQueries({ queryKey: CATS_KEY(householdId) })
      })

    return () => { supabase.removeChannel(channel) }
  }, [householdId, qc, isVisible])

  // ── mutations ─────────────────────────────────────────────────────────────────

  const addItem = useMutation({
    mutationKey: WRITES_KEY(householdId),
    // The id is generated by the caller and threaded through here so the optimistic
    // row and the inserted row share an id — that's what lets the realtime INSERT
    // echo dedupe instead of flashing a duplicate. Also makes retries idempotent.
    mutationFn: async (vars: AddVars) => {
      const insert = (categoryId: string | null) => supabase.from('items')
        .insert({ id: vars.id, household_id: householdId, name: vars.name,
                  category_id: categoryId, quantity: vars.quantity ?? null,
                  note: vars.note ?? null, created_by: memberId })
      let { error } = await insert(vars.category_id ?? null)
      // Its category is gone: the item goes to Other rather than nowhere.
      if (missingCategory(error, vars.category_id)) {
        moveToOther(vars.id)
        ;({ error } = await insert(null))
      }
      if (error) throw error
    },
    onMutate: async (vars) => {
      await holdReads()
      const optimistic: Item = {
        id: vars.id, household_id: householdId, name: vars.name,
        category_id: vars.category_id ?? null, quantity: vars.quantity ?? null,
        note: vars.note ?? null, done: false, done_at: null, created_by: memberId,
        sort_order: 0, updated_at: new Date().toISOString(), created_at: new Date().toISOString(),
        deleted_at: null,
      }
      qc.setQueryData<Item[]>(ITEMS_KEY(householdId), old => insertOptimistic(old ?? [], optimistic))
      return { snap: { before: undefined, index: -1 } as RowSnapshot<Item>, optimistic }
    },
    onError: (err, vars, ctx) => {
      revert(vars.id, ctx?.snap)
      if (isListFullError(err)) {
        if (ctx?.optimistic) setUnsaved(old => [...old.filter(i => i.id !== ctx.optimistic.id), ctx.optimistic])
        onListFull?.()
      }
    },
    // No onSuccess cache update: the realtime INSERT event reconciles the
    // optimistic row with the server's canonical copy.
    onSettled: () => readAgainIfCancelled(),
  })

  const toggleItem = useMutation({
    mutationKey: WRITES_KEY(householdId),
    mutationFn: async (vars: { id: string; done: boolean }) => {
      const { error } = await supabase.from('items').update({
        done: vars.done, done_at: vars.done ? new Date().toISOString() : null,
      }).eq('id', vars.id)
      if (error) throw error
    },
    onMutate: async (vars) => {
      await holdReads()
      const snap = snapshotRow(qc.getQueryData<Item[]>(ITEMS_KEY(householdId)) ?? [], vars.id)
      qc.setQueryData<Item[]>(ITEMS_KEY(householdId),
        old => toggleDone(old ?? [], vars.id, vars.done, undefined, memberId || null))
      return { snap }
    },
    onError: (err, vars, ctx) => {
      revert(vars.id, ctx?.snap)
      if (isListFullError(err)) onListFull?.()
    },
    onSettled: () => readAgainIfCancelled(),
  })

  const deleteItem = useMutation({
    mutationKey: WRITES_KEY(householdId),
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('items')
        .update({ deleted_at: new Date().toISOString() }).eq('id', id)
      if (error) throw error
    },
    onMutate: async (id) => {
      await holdReads()
      const snap = snapshotRow(qc.getQueryData<Item[]>(ITEMS_KEY(householdId)) ?? [], id)
      qc.setQueryData<Item[]>(ITEMS_KEY(householdId), old => old?.filter(i => i.id !== id))
      return { snap }
    },
    onError: (err, id, ctx) => {
      revert(id, ctx?.snap)
      if (isListFullError(err)) onListFull?.()
    },
    onSettled: () => readAgainIfCancelled(),
  })

  // Undo after a delete (ux/05 Delete). The delete only set deleted_at, so the
  // row is still there under the same id: clearing it brings the item back for
  // every device (realtime UPDATE) and the watch (its next delta carries the
  // row again, after the tombstone). An idempotent UPDATE, not a re-insert.
  // Restoring makes the item open again, so a full free list refuses it too.
  const restoreItem = useMutation({
    mutationKey: WRITES_KEY(householdId),
    mutationFn: async (vars: { item: Item; index: number }) => {
      const { error } = await supabase.from('items')
        .update({ deleted_at: null }).eq('id', vars.item.id)
      if (error) throw error
    },
    onMutate: async (vars) => {
      await holdReads()
      const snap = snapshotRow(qc.getQueryData<Item[]>(ITEMS_KEY(householdId)) ?? [], vars.item.id)
      qc.setQueryData<Item[]>(ITEMS_KEY(householdId),
        old => restoreAt(old ?? [], { ...vars.item, deleted_at: null }, vars.index))
      return { snap }
    },
    onError: (err, vars, ctx) => {
      revert(vars.item.id, ctx?.snap)
      if (isListFullError(err)) onListFull?.()
    },
    onSettled: () => readAgainIfCancelled(),
  })

  // `restore` folds the un-done into the same write as the edit: editing a done
  // suggestion's quantity and putting it back on the list is one user action, and
  // splitting it into update + toggle would race under the updated_at trigger.
  const updateItem = useMutation({
    mutationKey: WRITES_KEY(householdId),
    mutationFn: async (vars: { id: string; name: string; quantity: string | null; category_id: string | null; note?: string | null; restore?: boolean }) => {
      const update = (categoryId: string | null) => supabase.from('items').update({
        name: vars.name, quantity: vars.quantity, category_id: categoryId,
        ...(vars.note !== undefined ? { note: vars.note } : {}),
        ...(vars.restore ? { done: false, done_at: null } : {}),
      }).eq('id', vars.id)
      let { error } = await update(vars.category_id)
      if (missingCategory(error, vars.category_id)) {
        moveToOther(vars.id)
        ;({ error } = await update(null))
      }
      if (error) throw error
    },
    onMutate: async (vars) => {
      await holdReads()
      const snap = snapshotRow(qc.getQueryData<Item[]>(ITEMS_KEY(householdId)) ?? [], vars.id)
      qc.setQueryData<Item[]>(ITEMS_KEY(householdId), old => applyUpdate(old ?? [], vars))
      // A restore is an add, so it buzzes like one (matches toggleItem).
      if (vars.restore) haptic(HAPTICS.chip)
      return { snap }
    },
    onError: (err, vars, ctx) => {
      revert(vars.id, ctx?.snap)
      if (isListFullError(err)) onListFull?.()
    },
    onSettled: () => readAgainIfCancelled(),
  })

  const addCategory = useMutation({
    mutationFn: async (vars: { name: string; icon: string | null }) => {
      const cats = qc.getQueryData<Category[]>(CATS_KEY(householdId)) ?? []
      const maxOrder = cats.reduce((m, c) => Math.max(m, c.sort_order), -1)
      const { data, error } = await supabase.from('categories').insert({
        household_id: householdId, name: vars.name, icon: vars.icon, sort_order: maxOrder + 1,
      }).select().single()
      if (error) throw error
      return data as Category
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CATS_KEY(householdId) }),
  })

  const updateCategory = useMutation({
    mutationFn: async (vars: { id: string; name: string }) => {
      const { error } = await supabase.from('categories').update({ name: vars.name }).eq('id', vars.id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CATS_KEY(householdId) }),
  })

  // Through delete_category (049): the items — the list's and the history's —
  // and the recipes' picks move to `moveTo` (null: Other) in the same call.
  // Then everything that remembers categories is read again, so no stale guess
  // offers the category that's gone.
  const deleteCategory = useMutation({
    mutationFn: async (vars: { id: string; moveTo: string | null }) => {
      const { error } = await supabase.rpc('delete_category', { p_id: vars.id, p_move_to: vars.moveTo })
      if (error) throw error
    },
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: CATS_KEY(householdId) }),
      qc.invalidateQueries({ queryKey: ITEMS_KEY(householdId) }),
      qc.invalidateQueries({ queryKey: itemHistoryKey(householdId) }),
      qc.invalidateQueries({ queryKey: RECIPES_KEY(householdId) }),
    ]),
  })

  // The new order shows at once and goes back if the write fails.
  const reorderCategories = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.rpc('reorder_categories', { p_ids: ids })
      if (error) throw error
    },
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: CATS_KEY(householdId) })
      const before = qc.getQueryData<Category[]>(CATS_KEY(householdId))
      const place = new Map(ids.map((id, i) => [id, i]))
      qc.setQueryData<Category[]>(CATS_KEY(householdId), old => (old ?? [])
        .map(c => (place.has(c.id) ? { ...c, sort_order: place.get(c.id)! } : c))
        .sort((a, b) => a.sort_order - b.sort_order))
      return { before }
    },
    onError: (_err, _ids, ctx) => qc.setQueryData(CATS_KEY(householdId), ctx?.before),
  })

  // Finish trip (ux/05): the basket leaves the list. Soft-deleted, so each
  // row stays behind as a purchase that feeds the "Running low" chips
  // (lib/restock.ts). Resolves with what was bought; restores the basket if
  // the write fails.
  // Takes rows off the list as finished: soft-deleted, so they become history
  // (Running low, quick add's "From before"). Optimistic, put back on failure.
  const finishItems = useCallback(async (ids: string[], at: string = new Date().toISOString()): Promise<Item[]> => {
    const previous = qc.getQueryData<Item[]>(ITEMS_KEY(householdId)) ?? []
    const wanted = new Set(ids)
    const finished = previous.filter(i => wanted.has(i.id))
    if (!finished.length) return []
    await holdReads()
    qc.setQueryData<Item[]>(ITEMS_KEY(householdId), old => (old ?? []).filter(i => !wanted.has(i.id)))
    // The ids go in the URL (`id=in.(…)`); 300 of them is a 414 at the
    // gateway, so a big basket is written a chunk at a time. A failed chunk
    // puts back what wasn't written yet. One `at` for the lot: Finish trip
    // finishes them under the same time.
    for (let start = 0; start < finished.length; start += FINISH_CHUNK) {
      const chunk = finished.slice(start, start + FINISH_CHUNK)
      const { error } = await supabase.from('items').update({ deleted_at: at }).in('id', chunk.map(i => i.id))
      if (error) {
        const unwritten = finished.slice(start)
        qc.setQueryData<Item[]>(ITEMS_KEY(householdId), old => {
          const current = old ?? []
          return [...current, ...unwritten.filter(b => !current.some(i => i.id === b.id))]
        })
        readAgainIfCancelled(0)
        throw error
      }
    }
    readAgainIfCancelled(0)
    return finished
  }, [householdId, qc, holdReads, readAgainIfCancelled])

  // Finish trip: the whole basket leaves the list. The database's finish_trip
  // (045, useShopping.finish) takes it off in the same transaction as the
  // trip, for everyone's basket at once, so no rows are written here: the
  // basket leaves the screen at once, `write` finishes it on the server, and
  // a failed write puts it back. Resolves with what was bought.
  const finishBasket = useCallback(async (write: () => Promise<unknown>): Promise<Item[]> => {
    const previous = qc.getQueryData<Item[]>(ITEMS_KEY(householdId)) ?? []
    const bought = previous.filter(i => i.done)
    const ids = new Set(bought.map(i => i.id))
    await holdReads()
    qc.setQueryData<Item[]>(ITEMS_KEY(householdId), old => (old ?? []).filter(i => !ids.has(i.id)))
    try {
      await write()
    } catch (e) {
      qc.setQueryData<Item[]>(ITEMS_KEY(householdId), old => {
        const current = old ?? []
        return [...current, ...bought.filter(b => !current.some(i => i.id === b.id))]
      })
      throw e
    } finally {
      readAgainIfCancelled(0)
    }
    return bought
  }, [householdId, qc, holdReads, readAgainIfCancelled])

  const { mutate: add } = addItem
  const retryUnsaved = useCallback(() => {
    setUnsaved([])
    unsaved.forEach(i => add({ id: i.id, name: i.name, category_id: i.category_id, quantity: i.quantity, note: i.note }))
  }, [unsaved, add])

  return {
    items: itemsQuery.data ?? [],
    /** Adds a full free list refused, oldest first (see `unsaved` above). */
    unsaved,
    discardUnsaved: (id: string) => setUnsaved(old => old.filter(i => i.id !== id)),
    /** Writes every unsaved add again — after upgrading, or once there is room. */
    retryUnsaved,
    categories: catsQuery.data ?? [],
    isLoading: itemsQuery.isLoading || catsQuery.isLoading,
    /** Adds a row and returns its id (for Undo). */
    addItem: (name: string, category_id?: string | null, quantity?: string | null, note?: string | null) => {
      const id = uuid()
      addItem.mutate({ id, name, category_id, quantity, note })
      return id
    },
    toggleItem: (id: string, done: boolean) => toggleItem.mutate({ id, done }),
    deleteItem: (id: string) => deleteItem.mutate(id),
    updateItem: (id: string, name: string, quantity: string | null, category_id: string | null, restore = false, note?: string | null) =>
      updateItem.mutate({ id, name, quantity, category_id, restore, note }),
    restoreItem: (item: Item, index: number) => restoreItem.mutate({ item, index }),
    addCategory: (name: string, icon: string | null) => addCategory.mutateAsync({ name, icon }),
    renameCategory: (id: string, name: string) => updateCategory.mutateAsync({ id, name }),
    /** Deletes a category, its items moving to `moveTo` (null: Other). */
    deleteCategory: (id: string, moveTo: string | null) => deleteCategory.mutateAsync({ id, moveTo }),
    /** Sets the categories' order to `ids`'. */
    reorderCategories: (ids: string[]) => reorderCategories.mutateAsync(ids),
    finishBasket,
    finishItems,
  }
}
